import Button, { NavigableButton } from '@carvana/ds-button';
import cx from 'classnames';
import { useContext } from 'react';
import Image from '@/common/Image';
import RollingNumber from '@/common/RollingNumber';
import { useConfiguration } from '~/context/common/StaticContext';
import { useModalContext } from '~/context/new-cars/ModalContext/ModalContext';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import useClosestVehicle from '~/hooks/new-cars/selectors/useClosestVehicle';
import useSelectionsPrice from '~/hooks/new-cars/selectors/useSelectionsPrice';
import useVehicleImageUrl from '~/hooks/new-cars/selectors/useVehicleImageUrl';
import useVehicleName from '~/hooks/new-cars/selectors/useVehicleName';
import useTestDriveCenter from '~/hooks/new-cars/useTestDriveCenter';
import { FooterBar, FooterContent } from './StickyFooter.styles';

type OwnProps = {
  footerRef?: (node: HTMLDivElement | null) => void;
  isReleased: boolean;
  onOpen: () => void;
};

export default function Footer({ footerRef, isReleased, onOpen }: OwnProps) {
  const {
    activeSection,
    customizeOptions,
    selectedOptions,
    selectedTrim,
    trims
  } = useContext(ParentModelContext);
  const { enableNewCarConfiguratorV2 } = useConfiguration();
  const { setViewVehiclesModalOpen } = useModalContext();
  const closestVehicle = useClosestVehicle();
  const selectionsPrice = useSelectionsPrice();
  const testDriveCenter = useTestDriveCenter();
  const customizedTrim = customizeOptions.data;
  const rawClosestVehicle = customizedTrim.closestVehicle;
  const baseTrim =
    trims.data.results.find(trim => trim.priceAdd === 0) ??
    trims.data.results.at(0);

  const imageUrl = useVehicleImageUrl({
    width: 250
  });
  const vehicleName = useVehicleName();

  const price =
    selectionsPrice ||
    selectedTrim?.lowestPrice ||
    (baseTrim?.lowestPrice ?? 0);

  const showClosestVehicleLink =
    activeSection === 'options' &&
    !closestVehicle?.vehicleId &&
    Boolean(rawClosestVehicle?.vehicleId);

  const showMatchesButton =
    enableNewCarConfiguratorV2 &&
    !testDriveCenter.isActive &&
    activeSection === 'options' &&
    customizedTrim.totalMatchingVehicles > 0 &&
    false;

  return (
    <FooterBar
      ref={footerRef}
      className={cx({
        'mb-40 rounded-bl-2xl rounded-br-2xl sm:mb-3xl lg:mb-0': isReleased
      })}
    >
      <FooterContent
        isViewVehicleEnabled={Boolean(
          closestVehicle?.vehicleId || showClosestVehicleLink
        )}
      >
        <div className="flex flex-1 items-center gap-md sm:gap-lg">
          {imageUrl && (
            <Image
              alt={vehicleName}
              className="order-1 h-[45px] w-[70px] shrink-0 rounded-md bg-background-default object-cover sm:order-0 sm:h-[60px] sm:w-80"
              height={60}
              loading="eager"
              src={imageUrl}
              width={80}
            />
          )}

          <div className="flex flex-1 flex-col gap-xs sm:gap-none">
            <div className="flex flex-col gap-sm sm:items-start">
              <p className="t-heading-xs text-text-inverse sm:t-heading-sm">
                {vehicleName}
              </p>
              <p
                aria-label={`$${price.toLocaleString()}`}
                className="t-body-sm text-text-inverse"
                role="status"
              >
                <span
                  aria-hidden="true"
                  className="t-strong-sm inline-flex items-baseline"
                >
                  <span>$</span>
                  <RollingNumber key={price} value={price} />
                </span>
              </p>
            </div>
          </div>
        </div>

        {closestVehicle?.vehicleId && (
          <div className="flex w-full gap-md sm:w-auto">
            <Button
              className={cx(
                'flex-1 sm:flex-none w-full sm:w-[143px]',
                'shadow-none! border-2! border-(--decorative-2-strong)! bg-transparent! text-text-inverse!',
                'enabled:hover:border-transparent! enabled:hover:bg-decorative-2-strong! enabled:hover:text-text-strong!',
                'enabled:active:border-transparent! enabled:active:bg-(--icon-warning)! enabled:active:text-text-inverse!'
              )}
              data-analytics-attributes={JSON.stringify({
                level: 'Options',
                section: 'Sticky Footer',
                text: 'Review Price'
              })}
              data-analytics-id="New Cars"
              data-testid="footer-review-price"
              onClick={onOpen}
              size="sm"
              variant="secondaryInverted"
            >
              Review Price
            </Button>
            {testDriveCenter.isActive ? (
              <Button
                className="flex-1 sm:flex-none w-full sm:w-[143px]"
                data-analytics-attributes={JSON.stringify({
                  level: 'Options',
                  section: 'Sticky Footer',
                  text: 'Start Tour'
                })}
                data-analytics-id="New Cars TDC"
                data-testid="footer-start-tour"
                onClick={() => {
                  const data = customizedTrim;
                  testDriveCenter.sendBuild({
                    make: data.make || selectedOptions?.make || '',
                    model:
                      data.model ||
                      data.parentModel ||
                      selectedOptions?.parentModel ||
                      '',
                    year: data.year || selectedOptions?.year || 0,
                    trim: data.trim || selectedOptions?.trim || '',
                    color: closestVehicle?.exteriorColor?.name,
                    interior: closestVehicle?.interiorColor?.name,
                    drivetrain: closestVehicle?.driveType?.name,
                    options: (closestVehicle?.packageOptions || []).map(
                      o => o.name
                    )
                  });
                }}
                size="sm"
                variant="primaryInverted"
              >
                Start Tour
              </Button>
            ) : (
              <NavigableButton
                className="flex-1 sm:flex-none w-full sm:w-[143px]"
                data-analytics-attributes={JSON.stringify({
                  level: 'Options',
                  section: 'Sticky Footer',
                  text: 'View Vehicle'
                })}
                data-analytics-id="New Cars"
                data-testid="footer-view-vehicle"
                href={`/vehicle/${closestVehicle.vehicleId}`}
                size="sm"
                variant="primaryInverted"
              >
                View Vehicle
              </NavigableButton>
            )}
          </div>
        )}

        {showClosestVehicleLink && (
          <p className="t-body-sm text-text-inverse">
            <a
              className="underline"
              data-analytics-attributes={JSON.stringify({
                level: 'Options',
                section: 'Sticky Footer',
                text: 'View closest matching vehicle'
              })}
              data-analytics-id="New Cars"
              data-testid="footer-closest-vehicle-link"
              href={`/vehicle/${rawClosestVehicle?.vehicleId}`}
            >
              View closest matching vehicle
            </a>{' '}
            ({`$${rawClosestVehicle?.incentivizedPrice.toLocaleString()}`})
          </p>
        )}

        {showMatchesButton && (
          <Button
            className="w-full rounded-full! bg-background-default! text-text-primary! sm:w-auto"
            data-analytics-attributes={JSON.stringify({
              level: 'Options',
              section: 'Sticky Footer',
              text: 'View Matches'
            })}
            data-analytics-id="New Cars"
            data-testid="footer-view-matches"
            onClick={() => setViewVehiclesModalOpen(true)}
            size="sm"
            variant="primaryInverted"
          >
            {customizedTrim.totalMatchingVehicles} Match
            {customizedTrim.totalMatchingVehicles === 1 ? '' : 'es'}
          </Button>
        )}
      </FooterContent>
    </FooterBar>
  );
}
